import { generateId } from "@sfc/database/id-generation";

export { generateUserId } from "@sfc/database/id-generation";

/**
 * ID Prefix Reference
 * ===================
 * r-      Round (grant allocation cycle)
 * a-      Action (event in the event log)
 * audit-  General audit log
 * p-      Proposal (grant application)
 * field-  Proposal field (form question)
 * form-   Proposal form (collection of fields)
 * team-   Round-independent team
 * te-     Team event
 * gosnap- Grant ops sheet snapshot (a captured spreadsheet tab)
 * c-      Client (browser session)
 * f-      Funder (evaluator who allocates budget to recommenders)
 * rec-    Recommender (evaluator who rates proposals)
 * u-      User (person's account)
 * rm-     Round member (user's membership in a round)
 * fr-     Field response (proposal's answer to a field)
 * at-     Agent token (API access token)
 * aq-     AI question definition (saved custom proposal question)
 * aqr-    AI question run (per-proposal AI answer)
 * saq-    Shared AI question definition (round-shared custom proposal question)
 * saqr-   Shared AI question run (shared per-proposal AI answer)
 * ans-    Note score (LLM-scored evaluator note)
 * t-      Transcript (uploaded meeting transcript)
 * tch-    Transcript custom chapters (human-authored chapter set)
 * rts-    Round transcript summary (combined summary across meetings)
 * kpe-    Key people extraction (LLM-extracted key people)
 * kir-    Key individual research report (web-researched diligence memo)
 * pse-    Proposal summary extraction (LLM-extracted proposal summary)
 * fc-     Fact check (LLM-extracted claims from evaluator notes)
 * fcr-    Fact check claim response (human response to fact-check claim)
 * ssc-    Scoring sheet config (round → spreadsheet mapping)
 * pns-    Proposal note summary (per-proposal aggregated summary)
 * rns-    Recommender note summary (per-recommender aggregated summary)
 * rpns-   Recommender proposal note summary (per-proposal pyramid summary)
 * pt-     Prompt template (DB-managed LLM prompt)
 * se-     Summary edit (human edit to a proposal note summary, in proposal_note_summary_edits)
 * pdre-   Proposal deep research edit (human edit to a proposal deep research report)
 * kire-   Key individual research report edit (human edit to a KI research report)
 * sr-     Summary review (approval/changes-requested, in artifact_reviews)
 * sc-     Summary comment (comment, in artifact_comments)
 * ncr-    Note change review (triage status, in note_change_reviews)
 * ncc-    Note change comment (comment, in note_change_comments)
 * sfi-    Summary feedback issue (categorized QA issue, in artifact_feedback_issues)
 * as-     Artifact state (workflow + active edit pointer per artifact)
 * scic-   Summary common issues cache (cached AI issue summaries by round/hash)
 * lfi-    Longform image (imported image stored from Google Docs)
 * vp-     Video poster (a thumbnail candidate for a video)
 * e-      Evaluator (generic, rarely used)
 * cs-     Connection session (server-observed socket session)
 * ps-     Presence segment (client-reported visible stretch)
 * dm-     Discussion message (Spec Grants comment or reply, minted by the client)
 * sgq-    Spec Grants applicant question (minted by the client)
 */

export const generateRoundId = () => generateId("r", 8);
export const generateActionId = () => generateId("a");
// Watch segment (one contiguous video playback stretch, SP-2690). Minted by
// the client ONCE when a segment opens and reused for every extension/resend
// — the upsert idempotency key.
export const generateWatchSegmentId = () => generateId("ws");
export const generateConnectionSessionId = () => generateId("cs");
export const generatePresenceSegmentId = () => generateId("ps");
// Minted by the client when a comment or reply is sent, so whatever is queued
// behind it (a reply, a reaction) names that message even before the server
// confirms it.
export const generateDiscussionMessageId = () => generateId("dm");
// Minted by the client when a speculator or admin asks a Spec Grants applicant
// a question, for the same reason as a discussion message's.
export const generateApplicantQuestionId = () => generateId("sgq");
export const generateAuditLogId = () => generateId("audit");
export const generateProposalId = () => generateId("p", 10);
export const generateProposalFieldId = () => generateId("field", 10);
export const generateProposalFormId = () => generateId("form", 10);
export const generateTeamId = () => generateId("team", 10);
export const generateGrantOpsSnapshotId = () => generateId("gosnap");
export const generateGrantOpsPlanId = () => generateId("goplan");
export const generateGrantOpsRecipientId = () => generateId("go-rcp", 10);
export const generateTeamEventId = () => generateId("te");

// A stable id derived from a seed, for the ONE place that must never mint a
// random id: rebuilding a round from its event history. Reducers replay the
// same events on every load, so any id they invent must be identical each time
// or the round's own data stops matching itself. New rounds record their ids
// in the creation event; this covers only legacy events created before that,
// keyed off the round id so a given round always rebuilds to the same value.
// Not a source of new ids.
const DETERMINISTIC_ID_ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_";

function deterministicSuffix(seed: string, size: number): string {
  let hash = 0x811c9dc5;
  let out = "";

  for (let i = 0; out.length < size; i++) {
    hash ^= seed.charCodeAt(i % seed.length) + i * 0x100;
    hash = Math.imul(hash, 0x01000193) >>> 0;
    out += DETERMINISTIC_ID_ALPHABET[hash % DETERMINISTIC_ID_ALPHABET.length];
  }

  return out;
}

export const deriveLegacyProposalId = (seed: string) =>
  `p-${deterministicSuffix(seed, 10)}`;
export const deriveLegacyProposalFormId = (seed: string) =>
  `form-${deterministicSuffix(seed, 10)}`;
export const generateClientId = () => generateId("c", 8);
export const generateFunderId = () => generateId("f", 8);
export const generateRecommenderId = () => generateId("rec", 8);
export const generateRoundMemberId = () => generateId("rm", 10);
export const generateFieldResponseId = () => generateId("fr", 11);
export const generateAgentTokenId = () => generateId("at");
export const generateAiQuestionDefinitionId = () => generateId("aq");
export const generateAiQuestionRunId = () => generateId("aqr");
export const generateSharedAiQuestionDefinitionId = () => generateId("saq");
export const generateSharedAiQuestionRunId = () => generateId("saqr");
export const generateNoteScoreId = () => generateId("ans");
export const generateTranscriptId = () => generateId("t", 10);
export const generateTranscriptCustomChaptersId = () => generateId("tch");
export const generateVideoId = () => generateId("v", 10);
export const generateVideoPosterId = () => generateId("vp");
/** A FormUploadFile row — also the unique segment of its object path. */
export const generateFormUploadFileId = () => generateId("fu");
export const generateRoundTranscriptSummaryId = () => generateId("rts", 10);
export const generateKeyPeopleExtractionId = () => generateId("kpe");
export const generateKeyIndividualResearchReportId = () => generateId("kir");
export const generateProposalSummaryExtractionId = () => generateId("pse");
export const generateFactCheckId = () => generateId("fc");
export const generateFactCheckClaimResponseId = () => generateId("fcr");

export const generateNoteSentimentId = () => generateId("ns");
export const generateScoringSheetConfigId = () => generateId("ssc", 10);
export const generateProposalNoteSummaryId = () => generateId("pns");
export const generateRecommenderNoteSummaryId = () => generateId("rns");
export const generateRecommenderProposalNoteSummaryId = () =>
  generateId("rpns");
export const generatePromptTemplateId = () => generateId("pt");
export const generateSummaryEditId = () => generateId("se");
export const generateProposalDeepResearchEditId = () => generateId("pdre");
export const generateKeyIndividualResearchReportEditId = () =>
  generateId("kire");
export const generateSummaryReviewId = () => generateId("sr");
export const generateSummaryCommentId = () => generateId("sc");
export const generateNoteChangeReviewId = () => generateId("ncr");
export const generateNoteChangeCommentId = () => generateId("ncc");
export const generateSummaryFeedbackIssueId = () => generateId("sfi");
export const generateArtifactStateId = () => generateId("as");
export const generateSummaryCommonIssuesCacheId = () => generateId("scic");
export const generateLongformImageId = () => generateId("lfi");

export const generateEvaluatorId = (
  evaluatorType: "recommender" | "funder",
) => {
  switch (evaluatorType) {
    case "recommender":
      return generateRecommenderId();
    case "funder":
      return generateFunderId();
    default:
      return generateId("e", 8);
  }
};

export const generateTeamInvitationId = () => generateId("ti", 32);
